import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { useRealtime } from "./RealtimeContext";
import { gam } from "./gamificationApi";
import { toast } from "sonner";
import LevelUpModal from "../components/LevelUpModal";
import ShareMilestoneBurst from "../components/ShareMilestoneBurst";

const GamificationContext = createContext(null);

/**
 * Top-level provider that:
 *   1. Loads the current user's level on mount + when XP changes
 *   2. Listens for WS `xp:gained`, `level:up`, `challenge:completed`
 *   3. Shows a global level-up modal on level promotions
 *   4. Raises lightweight toasts on XP gains so progress always feels visible
 */
export function GamificationProvider({ children }) {
    const { user } = useAuth();
    const { subscribe } = useRealtime();
    const [level, setLevel] = useState(null);
    const [showLevelUp, setShowLevelUp] = useState(false);
    const [levelUpData, setLevelUpData] = useState(null);
    const [shareMilestone, setShareMilestone] = useState(null);

    const refreshLevel = useCallback(async () => {
        if (!user?.user_id) return;
        try {
            const data = await gam.level(user.user_id);
            setLevel(data);
        } catch {
            /* ignore */
        }
    }, [user?.user_id]);

    useEffect(() => { refreshLevel(); }, [refreshLevel]);

    // XP gain → toast + refresh level bar
    useEffect(() => {
        if (!user) return;
        const offXp = subscribe("xp:gained", ({ action, xp, level: lvl }) => {
            const label = ACTION_LABELS[action] || action;
            toast.success(`+${xp} XP — ${label}`, {
                description: lvl ? `${lvl.name} • ${lvl.xp.toLocaleString()} XP` : undefined,
            });
            if (lvl) setLevel(lvl);
        });
        const offLvl = subscribe("level:up", ({ level_before, level_after }) => {
            setLevelUpData({ before: level_before, after: level_after });
            setShowLevelUp(true);
        });
        const offCh = subscribe("challenge:completed", ({ challenge, xp }) => {
            toast.success(`Challenge complete: ${challenge.title}`, {
                description: `+${xp} XP awarded`,
            });
        });
        const offStamp = subscribe("passport:stamp", ({ label }) => {
            toast.success(`New passport stamp: ${label}`);
        });
        const offMilestone = subscribe("share:milestone", (payload) => {
            setShareMilestone(payload);
        });
        return () => { offXp(); offLvl(); offCh(); offStamp(); offMilestone(); };
    }, [user, subscribe]);

    return (
        <GamificationContext.Provider value={{ level, refreshLevel }}>
            {children}
            <LevelUpModal
                open={showLevelUp}
                onClose={() => setShowLevelUp(false)}
                before={levelUpData?.before}
                after={levelUpData?.after}
            />
            <ShareMilestoneBurst
                payload={shareMilestone}
                onDismiss={() => setShareMilestone(null)}
            />
        </GamificationContext.Provider>
    );
}

const ACTION_LABELS = {
    adventure_posted: "Adventure posted",
    gps_comment: "Trail note added",
    save_received: "Adventure saved by someone",
    review_written: "Review written",
    report_filed: "Safety report filed",
    weekly_challenge: "Weekly challenge complete",
    monthly_challenge: "Monthly challenge complete",
};

export function useGamification() {
    return useContext(GamificationContext) || { level: null, refreshLevel: () => {} };
}
